import React, { useState, useEffect, useCallback } from 'react';
import { Plus, UploadCloud } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import DataTable from '../../components/ui/DataTable';
import Modal from '../../components/ui/Modal';
import BulkUploadModal from '../../components/ui/BulkUploadModal';
import { useClient } from '../../context/ClientContext';
import { useAuth } from '../../context/AuthContext';

// Same fields, same order, same labels as the Add Product form above.
// The server matches headers case- and spacing-insensitively, so "Part Number",
// "part number" and "partNumber" all resolve to the same column.
const PRODUCT_TEMPLATE_HEADERS = [
  'Project Name', 'UOM', 'Part Number', 'Part Revision', 'DCN Level',
  'Product Name', 'Part Description', 'Category', 'HSN Code', 'Barcode',
  'Owner (Client)', 'Description',
];

const PRODUCT_TEMPLATE_REQUIRED = ['Project Name', 'Product Name'];

// One filled example row so the expected format of each column is obvious.
const PRODUCT_TEMPLATE_SAMPLE = [
  'U625', 'pcs', 'CAM-0000-2185', 'REV-02', 'DCN 1378',
  'Bypass Insert V2 RH', 'Bypass Insert V2 RH', 'Packaging', '4819', '890123456789',
  '', 'Optional notes',
];

const UNITS = ['pcs', 'kg', 'box', 'roll', 'ltr', 'mtr', 'set', 'pallet'];

const emptyForm = {
  projectName: '', name: '', partNumber: '', partDescription: '',
  partRevision: '', dcnLevel: '', category: '', unit: 'pcs',
  hsnCode: '', barcode: '', description: '', client: '',
};

// Project Name is stored as `sku` on the server; read any shape so records saved
// before the rename still display correctly.
const projectNameOf = (p) => p.projectName ?? p.projectNumber ?? p.sku ?? '';

const ProductList = () => {
  const { clients } = useClient();
  const { can } = useAuth();
  const canManage = can('products.manage');

  const [products, setProducts]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [clientFilter, setClientFilter] = useState('');
  const [search, setSearch]       = useState('');
  const [showForm, setShowForm]   = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm]           = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const fetchProducts = useCallback(() => {
    setLoading(true);
    const params = { all: 'true' };
    if (clientFilter) params.client = clientFilter;
    if (search) params.search = search;
    api.get('/products', { params })
      .then((r) => setProducts(r.data.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [clientFilter, search]);

  useEffect(() => {
    const t = setTimeout(fetchProducts, 250); // debounce search
    return () => clearTimeout(t);
  }, [fetchProducts]);

  const openCreate = () => { setEditTarget(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (p) => {
    setEditTarget(p);
    setForm({
      projectName: projectNameOf(p), name: p.name,
      partNumber: p.partNumber || '', partDescription: p.partDescription || '',
      partRevision: p.partRevision || '', dcnLevel: p.dcnLevel || '',
      category: p.category || '', unit: p.unit || 'pcs',
      hsnCode: p.hsnCode || '', barcode: p.barcode || '', description: p.description || '',
      client: p.client?._id || p.client || '',
    });
    setShowForm(true);
  };

  const handleToggle = async (p) => {
    try {
      await api.put(`/products/${p._id}`, { ...p, client: p.client?._id || p.client || '', isActive: !p.isActive });
      toast.success(`Product ${p.isActive ? 'deactivated' : 'activated'}`);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update product');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const payload = { ...form, client: form.client || null };
    try {
      if (editTarget) {
        await api.put(`/products/${editTarget._id}`, payload);
        toast.success('Product updated');
      } else {
        await api.post('/products', payload);
        toast.success('Product created');
      }
      setShowForm(false);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save product');
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      label: 'Project Name', key: 'projectName',
      render: (r) => <span className="font-mono text-zip-red text-xs font-semibold">{projectNameOf(r)}</span>,
    },
    {
      label: 'Part Number', key: 'partNumber',
      render: (r) => <span className="font-mono text-zip-text text-xs">{r.partNumber || '—'}</span>,
    },
    { label: 'Name', key: 'name', render: (r) => <span className="font-medium text-zip-text">{r.name}</span> },
    {
      label: 'Part Description', key: 'partDescription',
      render: (r) => <span className="text-zip-muted text-xs">{r.partDescription || '—'}</span>,
    },
    { label: 'Category', key: 'category', render: (r) => <span className="text-zip-muted text-xs">{r.category || '—'}</span> },
    { label: 'UOM', key: 'unit', render: (r) => <span className="font-mono text-zip-muted text-xs">{r.unit}</span> },
    {
      label: 'Part Rev. / DCN', key: 'partRevision',
      render: (r) => (
        <span className="font-mono text-zip-muted text-xs">
          {[r.partRevision, r.dcnLevel].filter(Boolean).join(' / ') || '—'}
        </span>
      ),
    },
    { label: 'HSN', key: 'hsnCode', render: (r) => <span className="font-mono text-zip-muted text-xs">{r.hsnCode || '—'}</span> },
    {
      label: 'Owner', key: 'client',
      render: (r) => r.client
        ? <span className="font-mono text-zip-muted text-xs">{r.client.code}</span>
        : <span className="chip-blue">Global</span>,
    },
    {
      label: 'Status', key: 'isActive',
      render: (r) => <span className={r.isActive ? 'chip-green' : 'chip-gray'}>{r.isActive ? 'Active' : 'Inactive'}</span>,
    },
    ...(canManage ? [{
      label: '', key: 'actions',
      render: (r) => (
        <div className="flex items-center gap-3">
          <button onClick={() => openEdit(r)} className="text-xs font-mono text-zip-muted hover:text-zip-text transition-colors">Edit</button>
          <button
            onClick={() => handleToggle(r)}
            className={`text-xs font-mono transition-colors ${r.isActive ? 'text-zip-muted hover:text-zip-red' : 'text-emerald-600 hover:text-emerald-700'}`}
          >
            {r.isActive ? 'Deactivate' : 'Activate'}
          </button>
        </div>
      ),
    }] : []),
  ];

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <input
            className="zip-input w-56"
            placeholder="Search project, part no., description…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select className="zip-input w-48" value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}>
            <option value="">All clients + global</option>
            {clients.map((c) => <option key={c._id} value={c._id}>{c.name} ({c.code})</option>)}
          </select>
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            <button onClick={() => setShowBulkUpload(true)} className="btn-ghost flex items-center gap-2 whitespace-nowrap">
              <UploadCloud size={15} /> Bulk Upload
            </button>
            <button onClick={openCreate} className="btn-primary flex items-center gap-2 whitespace-nowrap">
              <Plus size={15} /> Add Product
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="zip-card p-5 space-y-3">
          {[...Array(5)].map((_, i) => <div key={i} className="h-10 skeleton rounded" />)}
        </div>
      ) : (
        <DataTable columns={columns} rows={products} emptyText="No products in the catalog yet." />
      )}

      {/* Create / edit modal */}
      <Modal open={showForm} onClose={() => setShowForm(false)} title={editTarget ? 'Edit Product' : 'Add Product'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">Project Name *</label>
              <input className="zip-input font-mono uppercase" required placeholder="U625" maxLength={32}
                value={form.projectName} onChange={(e) => setForm({ ...form, projectName: e.target.value.toUpperCase() })}
                disabled={!!editTarget} />
            </div>
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">UOM</label>
              <select className="zip-input" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}>
                {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">Part Number</label>
              <input className="zip-input font-mono uppercase" placeholder="CAM-0000-2185" maxLength={64}
                value={form.partNumber} onChange={(e) => setForm({ ...form, partNumber: e.target.value.toUpperCase() })} />
            </div>
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">Part Revision / DCN Level</label>
              <div className="grid grid-cols-2 gap-2">
                <input className="zip-input" placeholder="REV-02"
                  value={form.partRevision} onChange={(e) => setForm({ ...form, partRevision: e.target.value })} />
                <input className="zip-input" placeholder="DCN 1378"
                  value={form.dcnLevel} onChange={(e) => setForm({ ...form, dcnLevel: e.target.value })} />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-zip-muted mb-1.5">Product Name *</label>
            <input className="zip-input" required placeholder="Corrugated Box (Large)"
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>

          <div>
            <label className="block text-xs font-medium text-zip-muted mb-1.5">Part Description</label>
            <input className="zip-input" placeholder="Bypass Insert V2 RH"
              value={form.partDescription} onChange={(e) => setForm({ ...form, partDescription: e.target.value })} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">Category</label>
              <input className="zip-input" placeholder="Packaging"
                value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">HSN Code</label>
              <input className="zip-input font-mono" placeholder="4819"
                value={form.hsnCode} onChange={(e) => setForm({ ...form, hsnCode: e.target.value })} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">Barcode</label>
              <input className="zip-input font-mono" placeholder="890123456789"
                value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">Owner (Client)</label>
              <select className="zip-input" value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })}>
                <option value="">Global (shared)</option>
                {clients.map((c) => <option key={c._id} value={c._id}>{c.name} ({c.code})</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-zip-muted mb-1.5">Description</label>
            <textarea className="zip-input resize-none" rows={2} placeholder="Optional notes…"
              value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>

          <div className="flex gap-3 pt-1">
            <button type="submit" disabled={submitting} className="btn-primary flex-1 flex items-center justify-center gap-2">
              {submitting
                ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Saving…</>
                : editTarget ? 'Save Changes' : 'Create Product'}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="btn-ghost">Cancel</button>
          </div>
        </form>
      </Modal>

      <BulkUploadModal
        open={showBulkUpload}
        onClose={() => setShowBulkUpload(false)}
        title="Bulk Upload Products"
        endpoint="/products/bulk-upload"
        templateHeaders={PRODUCT_TEMPLATE_HEADERS}
        templateSample={PRODUCT_TEMPLATE_SAMPLE}
        requiredColumns={PRODUCT_TEMPLATE_REQUIRED}
        templateFilename="products_template.csv"
        onDone={fetchProducts}
      />
    </div>
  );
};

export default ProductList;
